/**
 * Substrate Materializer
 *
 * Lazily reconstructs a single trace action's DOM (Playwright trace viewer
 * decode) and captures a production substrate-v2 snapshot from it in a
 * headless page — the offline equivalent of the live capture path.
 *
 * Expensive (decodes the trace + drives a headless browser), so results are
 * memoized per (traceZipPath, actionIndex, phase) and the reconstructed
 * dom.html is persisted next to the substrate for the locator verifier.
 *
 * @module optimizer/substrate-materializer
 */

import { mkdir, writeFile } from 'node:fs/promises';
import path from 'node:path';
import { pathToFileURL } from 'node:url';
import {
  parseTraceActions,
  reconstructSnapshots,
  toMutationIndex,
  type TraceAction,
  type TraceSnapshotPhase,
} from '@chatsdet/trace-tools';
import { captureSubstrateV2, parseSnapshot, type BrowserLogger } from '@chatsdet/browser-core';
import type { Page } from 'playwright';
import { createLogger } from '../logger.js';
import { HeadlessDomSession } from './headless-session.js';

const logger = createLogger({ module: 'SubstrateMaterializer' });

export interface MaterializedSubstrate {
  /** Production substrate-v2 yaml captured over the reconstructed DOM. */
  substrateYaml: string;
  /** Path to the persisted substrate yaml. */
  substratePath: string;
  /** Path to the persisted self-contained dom.html (verifier input). */
  domPath: string;
  actionIndex: number;
  phase: TraceSnapshotPhase;
}

export interface SubstrateMaterializerOptions {
  traceZipPath: string;
  /** Output root; files land under `<outDir>/<actionIndex>.<phase>.*`. */
  outDir: string;
  logger?: BrowserLogger;
  /**
   * Per-action wall-clock cap for trace reconstruction + substrate capture.
   * A single malformed trace snapshot must not consume an entire optimizer job.
   */
  timeoutMs?: number;
  /**
   * Shared headless session — pass the same instance to the LocatorVerifier
   * so capture and verification render identically (and one Chromium serves
   * both). When omitted the materializer owns (and closes) its own.
   */
  session?: HeadlessDomSession;
}

const DEFAULT_MATERIALIZE_TIMEOUT_MS = 30_000;
const PLAYWRIGHT_TARGET_SELECTOR = '[__playwright_target__]';

class MaterializeTimeoutError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'MaterializeTimeoutError';
  }
}

async function withTimeout<T>(
  promise: Promise<T>,
  description: string,
  timeoutMs: number
): Promise<T> {
  let timeout: ReturnType<typeof setTimeout> | null = null;
  const guarded = promise.catch((error: unknown) => {
    throw error;
  });
  const timeoutPromise = new Promise<never>((_, reject) => {
    timeout = setTimeout(() => {
      reject(new MaterializeTimeoutError(`${description} timed out after ${timeoutMs}ms`));
    }, timeoutMs);
  });

  try {
    return await Promise.race([guarded, timeoutPromise]);
  } finally {
    if (timeout) clearTimeout(timeout);
  }
}

/**
 * Memoizing materializer bound to one trace + output dir. Shares one headless
 * browser across calls; `close()` must run when the session ends.
 */
export class SubstrateMaterializer {
  private readonly cache = new Map<string, MaterializedSubstrate>();

  private readonly session: HeadlessDomSession;
  private readonly ownsSession: boolean;
  /** Parsed trace actions in `actions.jsonl` order — lazy, parsed once. */
  private allActions: TraceAction[] | null = null;
  /**
   * Serializes `materialize()` calls. Each call runs `reconstructSnapshots`,
   * which launches its own trace-viewer Chromium — concurrent calls (e.g. an
   * agent issuing parallel materialize tool calls) stack multiple Chromium
   * launches on one host and starve each other past every inner timeout
   * (cache-convergence ledger R7). Memoized results make serialized repeats
   * cheap, so callers keep their fire-in-parallel ergonomics.
   */
  private serial: Promise<unknown> = Promise.resolve();

  constructor(private readonly opts: SubstrateMaterializerOptions) {
    this.session = opts.session ?? new HeadlessDomSession();
    this.ownsSession = !opts.session;
  }

  /**
   * Reconstruct one action's snapshot and capture substrate-v2 over it.
   * `phase: 'input'` is the Playwright action-time snapshot and the preferred
   * authoring substrate because Playwright stamps the acted-on element there;
   * `'before'` and `'after'` preserve the legacy reconstruction semantics.
   *
   * `actionIndex` is the action's position in `parseTraceActions` order — the
   * SAME index space as the workspace's `browser/actions.jsonl` and
   * `optimizer/segments.json` actionIndices. The mutation-filtered index the
   * snapshot reconstructor uses internally is remapped here; observation
   * actions (screenshots) are rejected with a self-correcting error.
   */
  async materialize(
    actionIndex: number,
    phase: TraceSnapshotPhase = 'before'
  ): Promise<MaterializedSubstrate> {
    const hit = this.cache.get(`${actionIndex}:${phase}`);
    if (hit) return hit;
    const run = this.serial
      .catch(() => undefined)
      .then(() => this.materializeSerialized(actionIndex, phase));
    this.serial = run.catch(() => undefined);
    return run;
  }

  private async materializeSerialized(
    actionIndex: number,
    phase: TraceSnapshotPhase
  ): Promise<MaterializedSubstrate> {
    const key = `${actionIndex}:${phase}`;
    // Re-check after waiting in the queue: an identical call ahead of this one
    // may have already produced the result.
    const hit = this.cache.get(key);
    if (hit) return hit;

    this.allActions ??= parseTraceActions(this.opts.traceZipPath);
    const resolved = toMutationIndex(this.allActions, actionIndex);
    if (!resolved.ok) {
      throw new Error(resolved.reason);
    }

    const timeoutMs = this.opts.timeoutMs ?? DEFAULT_MATERIALIZE_TIMEOUT_MS;
    const results = await reconstructWithTimeoutRetry({
      traceZipPath: this.opts.traceZipPath,
      actionIndex,
      mutationIndex: resolved.mutationIndex,
      phase,
      timeoutMs,
    });
    const snapshot = results.find((r) => r.phase === phase && r.html);
    if (!snapshot?.html) {
      throw new Error(
        `No ${phase}-snapshot HTML reconstructable for trace action ${actionIndex} — ` +
          `the action may predate page load or the trace omitted its snapshot`
      );
    }

    await mkdir(this.opts.outDir, { recursive: true });
    // Resolve to an absolute path: callers may pass a relative outDir (e.g. the
    // CLI `--workspace ./dir`), and `file://<relative>` is an invalid URL that
    // fails `page.goto`. Absolute paths also make the returned dom/substrate
    // paths safe for the downstream locator verifier regardless of its cwd.
    const domPath = path.resolve(this.opts.outDir, `${actionIndex}.${phase}.dom.html`);
    await writeFile(domPath, snapshot.html, 'utf-8');

    const page = await this.session.page();
    try {
      page.setDefaultTimeout(timeoutMs);
      page.setDefaultNavigationTimeout(timeoutMs);
      const substrateYaml = await withTimeout(
        (async () => {
          await page.goto(pathToFileURL(domPath).href, { waitUntil: 'load', timeout: timeoutMs });
          await waitForTraceDomHydration(page);
          const repairedTraceName = await repairTraceTargetAccessibleName(
            page,
            this.allActions![actionIndex]!
          );
          if (repairedTraceName) {
            // The verifier reloads this file. Persist the same evidence-backed
            // repair used for substrate capture so authoring and verification
            // never observe different reconstructed DOMs.
            await writeFile(domPath, await page.content(), 'utf-8');
          }
          return captureSubstrateV2(page, this.opts.logger ?? logger);
        })(),
        `Substrate capture for action ${actionIndex} (${phase})`,
        timeoutMs
      );
      const substratePath = path.resolve(
        this.opts.outDir,
        `${actionIndex}.${phase}.substrate.v2.yaml`
      );
      await writeFile(substratePath, substrateYaml, 'utf-8');

      const out: MaterializedSubstrate = {
        substrateYaml,
        substratePath,
        domPath,
        actionIndex,
        phase,
      };
      this.cache.set(key, out);
      return out;
    } finally {
      await page.close().catch(() => undefined);
    }
  }

  async close(): Promise<void> {
    if (this.ownsSession) await this.session.close();
  }
}

async function reconstructWithTimeoutRetry(args: {
  traceZipPath: string;
  actionIndex: number;
  mutationIndex: number;
  phase: TraceSnapshotPhase;
  timeoutMs: number;
}) {
  const reconstruct = () =>
    withTimeout(
      reconstructSnapshots({
        traceZipPath: args.traceZipPath,
        includeScreenshots: false,
        includeHtml: true,
        filter: args.phase,
        raw: false,
        actionIndex: args.mutationIndex,
      }),
      `Trace snapshot reconstruction for action ${args.actionIndex} (${args.phase})`,
      args.timeoutMs
    );
  try {
    return await reconstruct();
  } catch (error) {
    if (!(error instanceof MaterializeTimeoutError)) throw error;
    return reconstruct();
  }
}

/**
 * Trace-rendered HTML installs load/DOMContentLoaded handlers that restore
 * values, scroll positions, canvas placeholders, and visibility. Two frames
 * gives those handlers a stable post-load accessibility tree before refs are
 * materialized. (Same wait the scenario-suite locator runner uses.)
 */
export async function waitForTraceDomHydration(page: {
  evaluate: (fn: () => Promise<void>) => Promise<void>;
}): Promise<void> {
  await page.evaluate(
    () =>
      new Promise<void>((resolve) => {
        requestAnimationFrame(() => requestAnimationFrame(() => resolve()));
      })
  );
}

export interface TraceRoleNameEvidence {
  role: string;
  name: string;
  caseSensitive: boolean;
}

/**
 * Parse only the simple literal role+name selector Playwright records for a
 * direct semantic locator. Chained selectors, regex names, and additional
 * predicates deliberately return null: they need more interpretation than
 * this reconstruction repair may safely perform.
 */
export function parseTraceRoleNameEvidence(selector: unknown): TraceRoleNameEvidence | null {
  if (typeof selector !== 'string') return null;
  const match = /^internal:role=([\w-]+)\[name=("(?:\\.|[^"])*")([is]?)\]$/.exec(selector.trim());
  if (!match) return null;

  try {
    const name = JSON.parse(match[2]) as unknown;
    if (typeof name !== 'string' || !name.trim()) return null;
    return {
      role: match[1],
      name,
      caseSensitive: match[3] !== 'i',
    };
  } catch {
    return null;
  }
}

export function shouldRepairTraceTargetName(args: {
  evidence: TraceRoleNameEvidence;
  actualRole: string;
  actualName: string;
}): boolean {
  if (args.actualRole !== args.evidence.role || !args.actualName) return false;

  const normalizeCase = (value: string): string =>
    args.evidence.caseSensitive ? value : value.toLocaleLowerCase('en-US');
  const expected = normalizeCase(args.evidence.name);
  const actual = normalizeCase(args.actualName);
  if (expected === actual) return false;

  const withoutWhitespace = (value: string): string => value.replace(/[\s\u00a0]+/gu, '');
  return withoutWhitespace(expected) === withoutWhitespace(actual);
}

/**
 * Playwright's trace reconstruction can omit whitespace-only text nodes that
 * were present in the live DOM. That changes a target's reconstructed
 * accessible name even though the trace action retains the live role+name
 * selector (for example, `Most common Acting As` becomes
 * `Most commonActing As`).
 *
 * Repair only the uniquely marked action target, and only when the recorded
 * and reconstructed names differ solely by whitespace. This is a
 * never-guess reconciliation: ordinary copy changes, camel-case names, regex
 * selectors, ambiguous markers, and role disagreements remain untouched.
 */
export async function repairTraceTargetAccessibleName(
  page: Pick<Page, 'locator'>,
  action: TraceAction
): Promise<boolean> {
  const evidence = parseTraceRoleNameEvidence(action.params?.selector);
  if (!evidence) return false;

  const marker = page.locator(PLAYWRIGHT_TARGET_SELECTOR);
  if ((await marker.count()) !== 1) return false;

  const [target] = parseSnapshot(await marker.ariaSnapshot());
  if (
    !target ||
    !shouldRepairTraceTargetName({
      evidence,
      actualRole: target.role,
      actualName: target.text ?? '',
    })
  ) {
    return false;
  }

  await marker.evaluate((element, name) => {
    element.setAttribute('aria-label', name);
  }, evidence.name);
  return true;
}
